/**
 * DS Color Variant Navigation
 *
 * Makes switching between color variants (separate products linked via the
 * custom.color_variants metafield) feel native: instead of a full page load,
 * the product-bound sections are re-fetched through the Shopify Section
 * Rendering API and swapped in place, wrapped in a View Transition when the
 * browser supports it. The URL is updated via pushState and back/forward
 * navigation is handled through popstate.
 *
 * The sections are replaced wholesale (not morphed) on purpose: the theme's
 * custom elements (product-variants, size-drawer, product-inventory, ...)
 * build their state in connectedCallback from the markup they are created
 * with. Replacing the sections gives every component a fresh, consistent
 * initialization for the new product.
 */
(() => {
  if (window.__dsColorVariantNav) return;
  window.__dsColorVariantNav = true;

  // Never intercept inside the theme editor
  if (window.Shopify && window.Shopify.designMode) return;

  const MAIN_SECTION_SELECTOR = '.shopify-section--main-product';
  // Every section on the product template that renders product-specific data.
  // App sections (e.g. reviews widget) are intentionally left out because
  // their third-party scripts do not re-initialize after a DOM swap.
  const SWAP_SECTION_SELECTORS = [
    MAIN_SECTION_SELECTOR,
    '.shopify-section--product-info',
    '.shopify-section--product-content',
    '.shopify-section--product-usp-content',
    '.shopify-section--product-usp-icons',
    '.shopify-section--product-recommendations',
  ];
  const LINK_SELECTOR = '.ds-product-variants__item a';
  const PREFETCH_TTL = 60000; // ms
  const IMAGE_PRELOAD_TIMEOUT = 1000; // ms
  const SECTIONS_PER_REQUEST = 5; // Section Rendering API limit
  const SIZE_STORAGE_KEY = 'ds-color-variants:size';
  const VARIANTS_READY_TIMEOUT = 4000; // ms
  // Static section (sections/ds-page-title.liquid) rendered in the target
  // page's context to read its real <title> / SEO title
  const TITLE_SECTION_ID = 'ds-page-title';

  let isSwapping = false;
  let hasMorphedProduct = false;
  const prefetchCache = new Map(); // url -> { promise, time }

  const getMainProductSection = () => document.querySelector(MAIN_SECTION_SELECTOR);

  const getSwappableSections = () =>
    Array.from(document.querySelectorAll(SWAP_SECTION_SELECTORS.join(', '))).filter((el) => el.id);

  const sectionIdFromElement = (element) => element.id.replace(/^shopify-section-/, '');

  const buildSectionsUrl = (url, sectionIds) => {
    const urlObject = new URL(url, window.location.origin);
    urlObject.searchParams.set('sections', sectionIds.join(','));
    return urlObject.toString();
  };

  const fetchJson = (url) => {
    const cached = prefetchCache.get(url);
    if (cached && Date.now() - cached.time < PREFETCH_TTL) {
      return cached.promise;
    }

    const promise = fetch(url).then((response) => {
      if (!response.ok) {
        throw new Error(`Section request failed with status ${response.status}`);
      }
      return response.json();
    });

    // Cache the in-flight promise so hover-prefetch and click share one request
    prefetchCache.set(url, { promise, time: Date.now() });
    promise.catch(() => prefetchCache.delete(url));

    return promise;
  };

  /**
   * Fetch the rendered HTML for the given section ids of the target URL.
   * Returns a map of sectionId -> HTML string. Chunked because the Section
   * Rendering API accepts at most 5 sections per request.
   */
  const fetchSections = async (targetUrl, sectionIds) => {
    const chunks = [];
    for (let i = 0; i < sectionIds.length; i += SECTIONS_PER_REQUEST) {
      chunks.push(sectionIds.slice(i, i + SECTIONS_PER_REQUEST));
    }

    const results = await Promise.all(
      chunks.map((chunk) => fetchJson(buildSectionsUrl(targetUrl, chunk)))
    );

    return Object.assign({}, ...results);
  };

  /**
   * Decode the eagerly loaded product images of the incoming section before
   * swapping, so the new content does not flash in empty. Capped by a timeout
   * to never block the switch on slow connections.
   */
  const preloadImages = (newMainSection) => {
    const images = Array.from(
      newMainSection.querySelectorAll('.ds-product-media__desktop img, .ds-product-media__mobile img')
    )
      .filter((img) => img.getAttribute('loading') !== 'lazy')
      .slice(0, 4);

    if (images.length === 0) return Promise.resolve();

    const decodes = images.map((img) => {
      const probe = new Image();
      const srcset = img.getAttribute('srcset');
      const sizes = img.getAttribute('sizes');
      if (srcset) probe.srcset = srcset;
      if (sizes) probe.sizes = sizes;
      probe.src = img.getAttribute('src') || '';
      return probe.decode().catch(() => {});
    });

    return Promise.race([
      Promise.all(decodes),
      new Promise((resolve) => setTimeout(resolve, IMAGE_PRELOAD_TIMEOUT)),
    ]);
  };

  // External widget scripts that must re-execute after a swap because they
  // mount their widgets at execution time (e.g. the reviews.io rating bar in
  // product-info). Safe to re-run: reviews.io skips elements that already
  // carry data-mounted, so widgets outside the swapped sections (announcement
  // bar) are left alone. Theme bundles stay excluded to avoid double binding.
  const REEXECUTE_SCRIPT_HOSTS = ['widget.reviews.io'];

  /**
   * Scripts inside markup inserted via DOMParser/importNode are inert;
   * recreate them so section-level behavior (sticky sidebar, cashback, ...)
   * re-binds to the new DOM. Inline scripts are always recreated; external
   * scripts (src) only when their host is on the re-execute allowlist.
   */
  const activateInlineScripts = (sectionElement) => {
    sectionElement.querySelectorAll('script').forEach((oldScript) => {
      // Some integrations install document/body-level delegated handlers and
      // must only be initialized once. Their existing instance keeps working
      // with the newly inserted markup and is notified below via a change event.
      if (oldScript.hasAttribute('data-ds-no-reexecute')) return;

      const src = oldScript.getAttribute('src');
      if (src && !REEXECUTE_SCRIPT_HOSTS.some((host) => src.includes(host))) return;

      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  };

  const parseSection = (html, expectedElementId) => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return parsedDocument.getElementById(expectedElementId);
  };

  /**
   * Extract the target page's real <title> from the rendered ds-page-title
   * section (mirrors the <title> logic in layout/theme.liquid). Falls back to
   * swapping the h1 text inside the current title if the section is missing.
   */
  const extractPageTitle = (sectionsHtml) => {
    const titleHtml = sectionsHtml[TITLE_SECTION_ID];
    if (!titleHtml) return null;

    const parsedDocument = new DOMParser().parseFromString(titleHtml, 'text/html');
    const titleElement = parsedDocument.querySelector('[data-ds-page-title]');
    if (!titleElement) return null;

    try {
      const title = JSON.parse(titleElement.textContent);
      if (typeof title !== 'string' || title.trim() === '') return null;
      // Shopify liefert page_title HTML-escaped aus (&#39;, &amp;, ...). Im
      // <title>-Tag dekodiert das der Browser beim Parsen, bei document.title
      // nicht - daher hier explizit dekodieren.
      return new DOMParser().parseFromString(title, 'text/html').documentElement.textContent;
    } catch (error) {
      return null;
    }
  };

  const updateDocumentTitle = (pageTitle, oldMainSection, newMainSection) => {
    if (pageTitle) {
      document.title = pageTitle;
      return;
    }

    // Fallback: swap the h1 text inside the current title
    const oldTitle = oldMainSection.querySelector('h1')?.textContent.trim();
    const newTitle = newMainSection.querySelector('h1')?.textContent.trim();
    if (oldTitle && newTitle && document.title.includes(oldTitle)) {
      document.title = document.title.replace(oldTitle, newTitle);
    }
  };

  /* ------------------------------------------------------------------ *
   * Size carry-over between color variants
   *
   * The Liquid snippet embeds a JSON map (color product URL -> variants
   * with size + availability) and renders the color links size-aware on
   * the server when a variant is selected. On the client we keep the
   * links in sync when the shopper picks a size (variant:changed) and
   * remember the choice in localStorage so it can be restored on a color
   * page of the same product family that was opened without a ?variant
   * parameter.
   * ------------------------------------------------------------------ */

  const readSizeMap = (scopeElement) => {
    const mapElement = scopeElement.querySelector('[data-ds-size-map]');
    if (!mapElement) return null;
    try {
      const map = JSON.parse(mapElement.textContent);
      return map && map.products ? map : null;
    } catch (error) {
      return null;
    }
  };

  const updateColorLinks = (sectionElement, size, map) => {
    sectionElement.querySelectorAll(LINK_SELECTOR).forEach((link) => {
      const baseUrl = link.dataset.baseUrl;
      const variants = baseUrl && map.products[baseUrl];
      if (!variants) return;

      const match = variants.find((variant) => variant.available && variant.size === size);
      link.setAttribute('href', match ? `${baseUrl}?variant=${match.id}` : baseUrl);
    });
  };

  const storeSize = (size, map) => {
    try {
      localStorage.setItem(
        SIZE_STORAGE_KEY,
        JSON.stringify({ size, urls: Object.keys(map.products) })
      );
    } catch (error) {
      // Storage unavailable (private mode, quota) - size still carries over via the links
    }
  };

  const waitFor = (predicate, timeout) =>
    new Promise((resolve) => {
      const startedAt = Date.now();
      const check = () => {
        if (predicate()) return resolve(true);
        if (Date.now() - startedAt > timeout) return resolve(false);
        setTimeout(check, 50);
      };
      check();
    });

  /**
   * Re-select the remembered size on a color page of the same product family
   * when the URL carries no ?variant parameter (e.g. size sold out on the
   * previous color's link, or the page was opened through normal navigation).
   */
  const restoreStoredSize = (sectionElement) => {
    if (new URL(window.location.href).searchParams.get('variant')) return;

    let stored;
    try {
      stored = JSON.parse(localStorage.getItem(SIZE_STORAGE_KEY) || 'null');
    } catch (error) {
      return;
    }
    if (!stored || !stored.size || !Array.isArray(stored.urls)) return;
    if (!stored.urls.includes(window.location.pathname)) return;

    const map = readSizeMap(sectionElement);
    const variants = map && map.products[window.location.pathname];
    if (!variants) return;

    const match = variants.find((variant) => variant.available && variant.size === stored.size);
    if (!match) return;

    const drawerItem = sectionElement.querySelector(`.ds-size-variant__item[data-id="${match.id}"]`);

    // Resolve the product-variants element the same way the size-drawer
    // click handler does, so we wait for the right instance to be ready
    const formId = drawerItem?.closest('size-drawer')?.getAttribute('data-form-id');
    const productVariants =
      (formId && sectionElement.querySelector(`product-variants[form-id="${formId}"]`)) ||
      sectionElement.querySelector('product-variants');
    if (!productVariants) return;

    // product-variants loads its product JSON asynchronously in connectedCallback
    waitFor(() => productVariants.product && productVariants.masterSelector, VARIANTS_READY_TIMEOUT).then(
      (ready) => {
        if (!ready || !sectionElement.isConnected) return;

        // Prefer the theme's own size-drawer flow (updates label, aria state, ...)
        if (drawerItem) {
          drawerItem.click();
        } else {
          productVariants.extSelectVariant(match.id);
        }
      }
    );
  };

  const selectedVariantIsUnavailable = (sectionElement, targetUrl) => {
    const variantInput = sectionElement.querySelector('form[action*="/cart/add"] input[name="id"]');
    const variantId = Number(variantInput?.value);
    if (!variantId) return false;

    const map = readSizeMap(sectionElement);
    const productPath = new URL(targetUrl, window.location.origin).pathname;
    const variants = map && map.products[productPath];
    const selectedVariant = variants && variants.find((variant) => variant.id === variantId);

    return selectedVariant ? !selectedVariant.available : false;
  };

  document.addEventListener('variant:changed', (event) => {
    if (!(event.target instanceof Element)) return;
    const sectionElement = event.target.closest(MAIN_SECTION_SELECTOR);
    if (!sectionElement) return;

    const variant = event.detail && event.detail.variant;
    if (!variant || !Array.isArray(variant.options)) return;

    // Klaviyo's legacy back-in-stock widget loads and retains the product JSON
    // from the initial page. It reacts to size changes, but cannot switch its
    // model to a different product after our section morph. Reload only when a
    // sold-out variant is selected on a morphed product so Klaviyo initializes
    // against the correct product; available purchase flows remain reload-free.
    if (hasMorphedProduct && !variant.available) {
      hasMorphedProduct = false;
      window.location.reload();
      return;
    }

    const map = readSizeMap(sectionElement);
    if (!map) return;

    const size = variant.options[map.sizeOptionIndex];
    if (size == null) return;

    storeSize(size, map);
    updateColorLinks(sectionElement, size, map);
  });

  const notifyThirdParties = (mainSectionElement, targetUrl) => {
    const variantInput = mainSectionElement.querySelector('form[action*="/cart/add"] input[name="id"]');
    variantInput?.dispatchEvent(new Event('change', { bubbles: true }));

    document.dispatchEvent(
      new CustomEvent('product:color-variant-switched', {
        bubbles: true,
        detail: { url: targetUrl },
      })
    );
  };

  const swapSections = async (targetUrl, { push }) => {
    const currentMainSection = getMainProductSection();
    if (!currentMainSection || !currentMainSection.id) {
      window.location.href = targetUrl;
      return;
    }

    if (isSwapping) return;
    isSwapping = true;
    document.documentElement.classList.add('ds-color-variant-loading');

    try {
      const currentSections = getSwappableSections();
      const sectionIds = currentSections.map(sectionIdFromElement).concat(TITLE_SECTION_ID);
      const sectionsHtml = await fetchSections(targetUrl, sectionIds);
      const newPageTitle = extractPageTitle(sectionsHtml);

      const replacements = [];
      let newMainSection = null;

      for (const currentSection of currentSections) {
        const html = sectionsHtml[sectionIdFromElement(currentSection)];
        if (!html) continue;

        const parsed = parseSection(html, currentSection.id);
        if (!parsed) continue;

        replacements.push([currentSection, parsed]);
        if (currentSection === currentMainSection) {
          newMainSection = parsed;
        }
      }

      if (!newMainSection) {
        throw new Error('Main product section not found in section rendering response');
      }

      await preloadImages(newMainSection);

      const scrollY = window.scrollY;
      let importedMainSection = null;

      const applySwap = () => {
        for (const [currentSection, parsed] of replacements) {
          const imported = document.importNode(parsed, true);
          // DOMParser parst mit deaktiviertem Scripting: <noscript>-Inhalte
          // (u.a. das Fallback-<select name="id"> des Produktformulars)
          // werden zu echten, aktiven DOM-Elementen und wuerden beim
          // Add-to-cart die erste Variante statt der gewaehlten submitten.
          // JS laeuft hier offensichtlich, also weg damit.
          imported.querySelectorAll('noscript').forEach((noscriptElement) => noscriptElement.remove());
          if (parsed === newMainSection) {
            importedMainSection = imported;
            updateDocumentTitle(newPageTitle, currentSection, imported);
          }
          currentSection.replaceWith(imported);
          activateInlineScripts(imported);
        }
        window.scrollTo(0, scrollY);
      };

      if (document.startViewTransition && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        try {
          await document.startViewTransition(applySwap).finished;
        } catch (transitionError) {
          // Transition skipped/interrupted, but the swap itself completed
        }
      } else {
        applySwap();
      }

      if (push) {
        history.scrollRestoration = 'manual';
        window.history.pushState({ dsColorVariant: true }, '', targetUrl);
      }

      hasMorphedProduct = true;

      // Covers a sold-out ?variant URL reached through browser back/forward or
      // another size-aware link. There is no variant:changed event in that case.
      if (selectedVariantIsUnavailable(importedMainSection, targetUrl)) {
        window.location.href = targetUrl;
        return;
      }

      window.Shopify?.PaymentButton?.init();
      notifyThirdParties(importedMainSection, targetUrl);
      restoreStoredSize(importedMainSection);
    } catch (error) {
      console.error('[ds-color-variants] Falling back to full navigation:', error);
      window.location.href = targetUrl;
    } finally {
      isSwapping = false;
      document.documentElement.classList.remove('ds-color-variant-loading');
    }
  };

  const prefetchTarget = (targetUrl) => {
    const sections = getSwappableSections();
    if (sections.length === 0) return;

    // Same id list (incl. title section) as the click flow so the request
    // URLs match and the prefetched response is reused from the cache
    const sectionIds = sections.map(sectionIdFromElement).concat(TITLE_SECTION_ID);
    fetchSections(targetUrl, sectionIds).catch(() => {});
  };

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest(LINK_SELECTOR);
    if (!link || !link.href) return;

    // Only intercept on the product page itself (not quick-buy, featured product, ...)
    const section = link.closest(MAIN_SECTION_SELECTOR);
    if (!section) return;

    if (!link.href.includes('/products/')) return;

    event.preventDefault();

    // Clicking the already active color is a no-op
    if (link.closest('.ds-product-variants__item-active')) return;

    swapSections(link.href, { push: true });
  });

  // Warm up the section cache on hover/touch so the click feels instant
  document.addEventListener(
    'pointerenter',
    (event) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest(LINK_SELECTOR);
      if (!link || !link.href || !link.closest(MAIN_SECTION_SELECTOR)) return;
      if (link.closest('.ds-product-variants__item-active')) return;

      prefetchTarget(link.href);
    },
    true
  );

  window.addEventListener('popstate', () => {
    if (!window.location.pathname.includes('/products/')) return;
    if (!getMainProductSection()) return;

    swapSections(window.location.href, { push: false });
  });

  // Restore the remembered size when a color page of the same product family
  // is opened through normal navigation (script is deferred, DOM is parsed)
  const initialSection = getMainProductSection();
  if (initialSection) {
    restoreStoredSize(initialSection);
  }
})();
